import { useEffect, useState } from 'react';
import type { StudioInfo, TakeInfo, TrackInfo } from '../../../shared/protocol.ts';
import { api, checkClipUrl, fileUrl, takeUrl, transcriptUrl } from '../lib/api.ts';
import { formatBytes, formatDuration } from '../lib/emitter.ts';
import { AppBar, Avatar, ConfirmDialog, Icon, useInterval, useTitle } from './common.tsx';

/** Host + producer view: every take, every participant's files, upload progress and downloads. */
export function FilesPage({ studioId, studioKey }: { studioId: string; studioKey: string }) {
  const [studio, setStudio] = useState<StudioInfo | null>(null);
  const [takes, setTakes] = useState<TakeInfo[] | null>(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<TrackInfo | null>(null);
  const [canTranscribe, setCanTranscribe] = useState(false);
  useEffect(() => void api.config().then((c) => setCanTranscribe(c.transcription)).catch(() => {}), []);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([api.studio(studioId, studioKey), api.takes(studioId, studioKey)]);
      setStudio(s);
      setTakes(t);
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => void load(), []);
  const busy = takes?.some(
    (t) =>
      !t.stoppedAt ||
      t.tracks.some((tr) => tr.status !== 'complete' && tr.status !== 'failed' && tr.status !== 'local') ||
      t.transcript?.status === 'queued' ||
      t.transcript?.status === 'running',
  );
  useInterval(() => void load(), busy ? 3000 : 15000);

  useTitle(studio ? `Files · ${studio.name}` : 'Files');

  if (error && !takes) {
    return (
      <div className="page">
        <AppBar />
        <main className="center-page">
          <div className="card">
            <h2>Can’t open files</h2>
            <p className="muted">{error}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="page">
      <AppBar>
        <span className="crumb-sep" />
        <span className="crumb ellipsis">{studio?.name ?? ''}</span>
        <span className="grow" />
        {studio?.role === 'host' && (
          <a className="btn sm" href={`/s/${studioId}?k=${studioKey}`}>
            <Icon name="arrowLeft" size={15} /> Back to studio
          </a>
        )}
      </AppBar>
      <main className="files">
        <div className="page-head">
          <div className="col" style={{ gap: 4 }}>
            <span className="eyebrow">Recordings</span>
            <h1>{studio?.name ?? '…'}</h1>
          </div>
          <div className="col" style={{ gap: 6, alignItems: 'flex-end' }}>
            {takes && takes.length > 0 && (
              <span className="muted small">
                {takes.length} take{takes.length === 1 ? '' : 's'} · {formatBytes(takes.reduce((a, t) => a + t.tracks.reduce((b, tr) => b + (tr.size ?? tr.receivedBytes), 0), 0))}
              </span>
            )}
            {studio?.role === 'host' && <Retention studio={studio} studioKey={studioKey} onChange={load} />}
            {(studio?.role === 'host' || studio?.role === 'producer') && <WhereRecordingsGo studioId={studioId} studioKey={studioKey} />}
          </div>
        </div>
        {!takes && (
          <div className="card row muted">
            <span className="spinner" /> Loading recordings…
          </div>
        )}
        {takes && !takes.length && (
          <div className="card empty" style={{ border: '1px solid var(--line)' }}>
            <Icon name="film" size={26} />
            <strong style={{ color: 'var(--text)' }}>No takes yet</strong>
            Start a recording in the studio. Files appear here as they upload.
          </div>
        )}
        {takes?.map((take) => (
          <TakeCard key={take.id} take={take} studioKey={studioKey} onPreview={setPreview} canTranscribe={canTranscribe} canDelete={studio?.role === 'host'} onChange={load} />
        ))}
      </main>
      {preview && (
        <div className="modal-back" onClick={(e) => e.target === e.currentTarget && setPreview(null)}>
          <div className="modal">
            <header>
              <Avatar name={preview.participantName} size={30} />
              <div className="col grow" style={{ gap: 1 }}>
                <h2>
                  {preview.participantName} · {preview.label}
                </h2>
                <span className="faint small mono">{preview.fileName}</span>
              </div>
              <button className="ghost icon" onClick={() => setPreview(null)} aria-label="Close">
                <Icon name="close" />
              </button>
            </header>
            <div className="content">
              {preview.ext === 'wav' ? (
                <audio controls autoPlay src={fileUrl(preview.id, studioKey)} style={{ width: '100%' }} />
              ) : (
                <video controls autoPlay playsInline src={fileUrl(preview.id, studioKey)} style={{ width: '100%', maxHeight: '70vh', background: '#000', borderRadius: 10 }} />
              )}
              {preview.status !== 'complete' && (
                <div className="alert info small">
                  <Icon name="info" size={14} /> Still uploading: you’re previewing the {formatBytes(preview.receivedBytes)} received so far.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const RETENTION: { days: number | null; label: string }[] = [
  { days: null, label: 'until you delete them' },
  { days: 30, label: 'for 30 days' },
  { days: 90, label: 'for 90 days' },
  { days: 365, label: 'for a year' },
];

/** Host: how long this studio keeps recordings. Owners are emailed 14 and 3 days before anything goes. */
function Retention({ studio, studioKey, onChange }: { studio: StudioInfo; studioKey: string; onChange: () => void }) {
  const [error, setError] = useState('');
  const current = studio.retentionDays ?? null;
  const options = RETENTION.some((o) => o.days === current) ? RETENTION : [...RETENTION, { days: current, label: `for ${current} days` }];
  return (
    <label
      className="row small muted"
      style={{ gap: 6 }}
      title={studio.canSetRetention ? 'You’re emailed 14 days and 3 days before a take is deleted, and can keep it' : 'Sign in as the studio’s owner to choose this'}
    >
      Keep recordings
      <select
        className="sm"
        value={current ?? ''}
        disabled={!studio.canSetRetention}
        onChange={async (e) => {
          setError('');
          try {
            await api.setRetention(studio.id, studioKey, e.target.value ? Number(e.target.value) : null);
            onChange();
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      >
        {options.map((o) => (
          <option key={o.days ?? 'forever'} value={o.days ?? ''}>
            {o.label}
          </option>
        ))}
      </select>
      {error && <span style={{ color: 'var(--bad)' }}>{error}</span>}
    </label>
  );
}

/** Within this of its deletion, a take shows a warning (the owner's emails go out at 14 and 3 days). */
const WARN_MS = 14 * 86_400_000 + 3_600_000;

/** Hosts and producers: whether new takes upload, or stay on each person's computer. */
function WhereRecordingsGo({ studioId, studioKey }: { studioId: string; studioKey: string }) {
  const [keepLocal, setKeepLocal] = useState<boolean | null>(null);
  useEffect(() => void api.studioSettings(studioId, studioKey).then((s) => setKeepLocal(s.keepLocal)).catch(() => {}), [studioId]);
  if (keepLocal == null) return null;
  return (
    <label className="row small muted" style={{ gap: 6 }} title="Applies from the next take">
      Recordings
      <select
        className="sm"
        value={keepLocal ? 'local' : 'upload'}
        onChange={async (e) => {
          const next = e.target.value === 'local';
          setKeepLocal(next);
          setKeepLocal((await api.updateStudioSettings(studioId, studioKey, { keepLocal: next })).keepLocal);
        }}
      >
        <option value="upload">upload to the studio</option>
        <option value="local">stay on each person’s computer</option>
      </select>
    </label>
  );
}

/** A take that's soon to be deleted: when, why, and (for the host) keeping it. */
function DeletionNotice({ take, studioKey, canKeep, onChange }: { take: TakeInfo; studioKey: string; canKeep: boolean; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  if (!take.deletesAt || take.deletesAt - Date.now() > WARN_MS) return null;
  const when = new Date(take.deletesAt).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  const days = Math.max(0, Math.round((take.deletesAt - Date.now()) / 86_400_000));
  const credit = take.deleteReason === 'credit';
  return (
    <div className="take-section">
      <div className={`alert ${days <= 3 ? 'bad' : 'warn'}`} style={{ alignItems: 'center' }}>
        <Icon name="clock" size={16} />
        <span className="grow">
          To be deleted on {when} ({days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`}).{' '}
          {credit ? 'The studio is out of storage credit.' : 'This studio keeps recordings for a set time.'}
        </span>
        {canKeep && !credit && (
          <button
            className="sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.keepTake(take.id, studioKey);
                onChange();
              } finally {
                setBusy(false);
              }
            }}
          >
            Keep this take
          </button>
        )}
      </div>
    </div>
  );
}

function TakeCard({
  take,
  studioKey,
  onPreview,
  canTranscribe,
  canDelete,
  onChange,
}: {
  take: TakeInfo;
  studioKey: string;
  onPreview: (t: TrackInfo) => void;
  canTranscribe: boolean;
  canDelete: boolean;
  onChange: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const total = take.tracks.reduce((a, t) => a + (t.size ?? t.receivedBytes), 0);
  const received = take.tracks.reduce((a, t) => a + Math.min(t.receivedBytes, t.size ?? t.receivedBytes), 0);
  const complete = take.tracks.length > 0 && take.tracks.every((t) => t.status === 'complete' || t.status === 'failed');
  // Files of a keep-local take that nobody has uploaded: on the computers they were recorded on.
  const onComputers = take.tracks.filter((t) => t.status === 'local').length;
  const participants = new Set(take.tracks.map((t) => t.participantId)).size;
  const duration = (take.stoppedAt ?? Date.now()) - take.startedAt;
  const checks = take.checks.filter((c) => c.clips.length);
  const started = new Date(take.startedAt);

  return (
    <section className="card take">
      <div className="take-head">
        <div className="col" style={{ gap: 6 }}>
          <div className="row" style={{ gap: 10 }}>
            <h2>Take {take.number}</h2>
            {!take.stoppedAt ? (
              <span className="pill rec">
                <span className="dot pulse" /> Recording
              </span>
            ) : onComputers ? (
              <span className="pill" title="This take’s files stay on the computers they were recorded on, unless someone uploads them from “On this computer”">
                <Icon name="folder" size={12} /> {onComputers === take.tracks.length ? 'On participants’ computers' : `${onComputers} file${onComputers > 1 ? 's' : ''} on participants’ computers`}
              </span>
            ) : complete ? (
              <span className="pill ok">
                <Icon name="check" size={12} /> All files uploaded
              </span>
            ) : (
              <span className="pill accent tnum">
                <Icon name="upload" size={12} /> {total ? Math.floor((received / total) * 100) : 0}% uploaded
              </span>
            )}
          </div>
          <div className="take-meta">
            <span>
              <Icon name="clock" size={13} /> {started.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })},{' '}
              {started.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="tnum">{formatDuration(duration)} long</span>
            <span>
              {participants} participant{participants === 1 ? '' : 's'}
            </span>
            <span>
              {take.tracks.length} file{take.tracks.length === 1 ? '' : 's'} · {formatBytes(total)}
            </span>
            {take.deletesAt && take.deletesAt - Date.now() > WARN_MS && (
              <span title="This studio keeps recordings for a set time. You’ll be emailed 14 and 3 days before.">
                <Icon name="clock" size={13} /> Kept until {new Date(take.deletesAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
            {take.coldSince && (
              <span title="Older recordings are kept in cheaper long-term storage. Downloads work as before.">
                <Icon name="layers" size={13} /> Long-term storage
              </span>
            )}
          </div>
        </div>
        <div className="row wrap">
          <div className="btn-group">
            <a className="btn" href={takeUrl(take.id, studioKey, 'timeline.fcpxml')} title="Import into DaVinci Resolve, Final Cut Pro or Premiere">
              <Icon name="timeline" size={15} /> Timeline (.fcpxml)
            </a>
            <a className="btn" href={takeUrl(take.id, studioKey, 'manifest.json')} target="_blank" rel="noreferrer" title="Every file's offset, format and checks, as JSON">
              Manifest
            </a>
          </div>
          <a className="btn primary" href={takeUrl(take.id, studioKey, 'archive.zip')} title="Every file plus manifest, FCPXML timeline and event log">
            <Icon name="download" size={16} /> Download all (.zip)
          </a>
          {canDelete && (
            <button
              className="ghost icon danger"
              onClick={() => setConfirming(true)}
              disabled={!take.stoppedAt}
              title={take.stoppedAt ? 'Delete this take' : 'Stop recording before deleting'}
              aria-label={`Delete take ${take.number}`}
            >
              <Icon name="trash" size={16} />
            </button>
          )}
        </div>
      </div>
      {confirming && (
        <ConfirmDialog
          title={`Delete take ${take.number}?`}
          confirmLabel="Delete take"
          onClose={() => setConfirming(false)}
          onConfirm={async () => {
            await api.deleteTake(take.id, studioKey);
            onChange();
          }}
        >
          <p>
            Its {take.tracks.length} file{take.tracks.length === 1 ? '' : 's'} ({formatBytes(total)}), transcript and timeline are removed from
            Wordstow for good. This can’t be undone.
          </p>
          <p className="muted small">Copies still on participants’ own computers aren’t affected.</p>
        </ConfirmDialog>
      )}

      <DeletionNotice take={take} studioKey={studioKey} canKeep={canDelete} onChange={onChange} />

      <div className="take-section">
        <Timeline take={take} />
      </div>

      <TranscriptRow take={take} studioKey={studioKey} canTranscribe={canTranscribe} onChange={onChange} />

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ paddingLeft: 20 }}>Participant</th>
              <th>Source</th>
              <th>Starts at</th>
              <th>Length</th>
              <th>Format</th>
              <th style={{ width: 190 }}>Upload</th>
              <th style={{ paddingRight: 20 }} />
            </tr>
          </thead>
          <tbody>
            {take.tracks.map((t) => {
              const size = t.size ?? null;
              const pct = size ? Math.min(100, (t.receivedBytes / size) * 100) : null;
              const offset = t.anchorServerMs != null ? t.anchorServerMs - take.startedAt : null;
              const kind = t.sourceKind === 'microphone' ? 'audio' : t.sourceKind === 'screen' ? 'screen' : '';
              return (
                <tr key={t.id}>
                  <td style={{ paddingLeft: 20 }}>
                    <span className="person">
                      <Avatar name={t.participantName} size={26} />
                      <span className="col" style={{ gap: 0 }}>
                        <span>{t.participantName}</span>
                        {t.participantEmail && <span className="faint small">{t.participantEmail}</span>}
                      </span>
                    </span>
                  </td>
                  <td>
                    <div className="col" style={{ gap: 1 }}>
                      <span className="row" style={{ gap: 7 }}>
                        <span className={`kind-dot ${kind}`} />
                        {t.label}
                      </span>
                      <span className="faint small mono">{t.fileName}</span>
                    </div>
                  </td>
                  <td>
                    <div className="col" style={{ gap: 2 }}>
                      <span className="mono tnum">{offset != null ? `${offset >= 0 ? '+' : ''}${(offset / 1000).toFixed(3)} s` : '–'}</span>
                      <SyncNote track={t} />
                    </div>
                  </td>
                  <td className="tnum">{t.durationMs != null ? formatDuration(t.durationMs) : '–'}</td>
                  <td className="small muted">
                    <div className="col" style={{ gap: 3, alignItems: 'flex-start' }}>
                      <span>
                        {t.width ? `${t.width}×${t.height} · ${t.frameRate} fps` : t.sampleRate ? `${t.sampleRate / 1000} kHz · ${t.channels} ch` : ''}
                        {t.codec ? <span className="faint"> · {t.codec.split('.')[0]}</span> : ''}
                      </span>
                      {t.droppedFrames ? <span className="pill warn">{t.droppedFrames} dropped frame{t.droppedFrames === 1 ? '' : 's'}</span> : null}
                    </div>
                  </td>
                  <td>
                    <div className="col" style={{ gap: 5 }}>
                      {t.status !== 'local' && (
                        <div className={`bar ${t.status === 'complete' ? 'ok' : ''}`}>
                          <i style={{ width: `${pct ?? 100}%`, opacity: pct == null ? 0.35 : 1 }} />
                        </div>
                      )}
                      <span className="small muted tnum">
                        {t.status === 'local'
                          ? `On ${t.participantName}’s computer · ${formatBytes(size ?? 0)}`
                          : t.status === 'complete'
                          ? `Done · ${formatBytes(t.receivedBytes)}`
                          : t.status === 'failed'
                            ? 'Nothing recorded'
                            : t.status === 'recording'
                              ? `${formatBytes(t.receivedBytes)} · still recording`
                              : `${formatBytes(t.receivedBytes)} of ${formatBytes(size ?? 0)}`}
                      </span>
                    </div>
                  </td>
                  <td style={{ paddingRight: 20 }}>
                    <div className="row" style={{ justifyContent: 'flex-end', gap: 4 }}>
                      <button className="ghost sm icon" disabled={!t.receivedBytes} onClick={() => onPreview(t)} title="Preview (works while uploading)" aria-label="Preview">
                        <Icon name="play" size={13} />
                      </button>
                      <a className="btn sm icon" href={t.receivedBytes ? fileUrl(t.id, studioKey, true) : undefined} aria-disabled={!t.receivedBytes} title="Download" aria-label="Download">
                        <Icon name="download" size={15} />
                      </a>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {checks.length > 0 && (
        <div className="take-section" style={{ borderTop: '1px solid var(--line)' }}>
          <details>
            <summary className="muted small">
              Checks during this take ({checks.length})
            </summary>
            {checks.map((c) => (
              <div key={c.id} className="col" style={{ marginTop: 12 }}>
                <span className="small faint">Requested at {new Date(c.requestedAt).toLocaleTimeString()}</span>
                <div className="check-grid">
                  {c.clips.map((clip) => (
                    <div key={clip.id} className="col small" style={{ gap: 5 }}>
                      {clip.mime.startsWith('audio') ? <audio controls src={checkClipUrl(clip.id, studioKey)} /> : <video controls muted src={checkClipUrl(clip.id, studioKey)} />}
                      <span className="ellipsis">
                        <strong>{clip.participantName}</strong> <span className="muted">· {clip.label}</span>
                      </span>
                      <span className={`pill verdict ${clip.verdict.ok ? 'ok' : 'bad'}`}>
                        <Icon name={clip.verdict.ok ? 'check' : 'warn'} size={12} /> {clip.verdict.summary}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </details>
        </div>
      )}
    </section>
  );
}

/** Per-stream transcripts merged onto the take timeline (analysis/transcribe_take.py on the server). */
function TranscriptRow({ take, studioKey, canTranscribe, onChange }: { take: TakeInfo; studioKey: string; canTranscribe: boolean; onChange: () => void }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState('');
  const tr = take.transcript;
  const mics = take.tracks.filter((t) => t.sourceKind === 'microphone');
  const ready = mics.length > 0 && mics.every((t) => t.status === 'complete');
  if (!canTranscribe && !tr) return null;
  const start = async () => {
    setError('');
    try {
      await api.transcribe(take.id, studioKey);
      onChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const has = (f: string) => tr?.files.includes(f);
  return (
    <div className="take-section">
      <div className="row wrap" style={{ gap: 8 }}>
        <Icon name="fileText" size={16} />
        <strong style={{ marginRight: 4 }}>Transcript</strong>
        {!tr && (
          <>
            <span className="muted small grow">{ready ? 'Each microphone is transcribed separately, then merged on the take timeline.' : mics.some((t) => t.status === 'local') ? 'Needs the microphone files uploaded (they’re on participants’ computers).' : 'Available once every microphone file has uploaded.'}</span>
            <button className="sm" onClick={start} disabled={!ready}>
              Transcribe take
            </button>
          </>
        )}
        {(tr?.status === 'queued' || tr?.status === 'running') && (
          <span className="pill accent">
            <span className="spinner" style={{ width: 10, height: 10, borderWidth: 1.5 }} /> {tr.status === 'queued' ? 'Queued' : 'Transcribing…'}
          </span>
        )}
        {tr?.status === 'failed' && (
          <>
            <span className="pill bad grow" title={tr.error} style={{ flex: '0 1 auto' }}>
              <span className="ellipsis">Failed: {tr.error?.slice(0, 80)}</span>
            </span>
            <span className="grow" />
            <button className="sm" onClick={start}>Retry</button>
          </>
        )}
        {tr?.status === 'done' && (
          <>
            <span className="pill ok">
              <Icon name="check" size={12} /> Ready
            </span>
            <span className="grow" />
            {has('transcript.md') && (
              <button className="sm" onClick={async () => setText(text ? null : await (await fetch(transcriptUrl(take.id, studioKey, 'transcript.md'))).text())}>
                {text ? 'Hide' : 'Read'}
              </button>
            )}
            <div className="btn-group">
              {has('transcript.md') && <a className="btn sm" href={transcriptUrl(take.id, studioKey, 'transcript.md')} download>Text (.md)</a>}
              {has('transcript.srt') && <a className="btn sm" href={transcriptUrl(take.id, studioKey, 'transcript.srt')} download>Subtitles (.srt)</a>}
              {has('transcript.json') && (
                <a className="btn sm" href={transcriptUrl(take.id, studioKey, 'transcript.json')} download title="Every word with take-timeline times, for text-based editing">
                  Words (.json)
                </a>
              )}
            </div>
            {canTranscribe && <button className="ghost sm" onClick={start}>Run again</button>}
          </>
        )}
        {error && <span className="pill bad">{error}</span>}
      </div>
      {text && <div className="transcript-text">{text}</div>}
    </div>
  );
}

/** Do this file's take-wide sync marks agree with where it was placed? */
function SyncNote({ track: t }: { track: TrackInfo }) {
  const n = t.syncMarks.length;
  if (t.placement === 'marks')
    return (
      <span className="sync-note warn" title="This file's measured start was lost (e.g. a crash), so it was placed from its sync marks">
        <Icon name="flag" size={11} /> Placed by {n} sync mark{n === 1 ? '' : 's'}
      </span>
    );
  if (!n || t.markAgreementMs == null) return <span className="sync-note faint">No sync marks</span>;
  const ok = Math.abs(t.markAgreementMs) <= 20;
  return (
    <span
      className={`sync-note ${ok ? 'ok' : 'warn'}`}
      title={`${n} take-wide sync marks in this file put its start ${t.markAgreementMs >= 0 ? '+' : ''}${t.markAgreementMs} ms from its measured start`}
    >
      <Icon name={ok ? 'check' : 'warn'} size={11} /> {n} mark{n === 1 ? '' : 's'} · {ok ? 'agree' : 'differ'} {Math.abs(t.markAgreementMs) < 1 ? '<1' : Math.round(Math.abs(t.markAgreementMs))} ms
    </span>
  );
}

const TICK_STEPS_S = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];

/** Where each file sits on the shared take timeline: late joiners and mid-take shares show up offset. */
function Timeline({ take }: { take: TakeInfo }) {
  const placed = take.tracks
    .filter((t) => t.anchorServerMs != null)
    .map((t) => ({ t, start: t.anchorServerMs! - take.startedAt, len: t.durationMs ?? 0 }));
  if (!placed.length) return null;
  const end = Math.max((take.stoppedAt ?? Date.now()) - take.startedAt, ...placed.map((p) => p.start + p.len), 1);
  const pct = (ms: number) => `${Math.max(0, (ms / end) * 100)}%`;
  const step = (TICK_STEPS_S.find((s) => end / 1000 / s <= 8) ?? 3600) * 1000;
  const ticks: number[] = [];
  for (let t = 0; t < end - step * 0.4; t += step) ticks.push(t);
  return (
    <div className="timeline" aria-label="Take timeline">
      <div className="tl-names">
        <div className="tl-head">
          <span className="eyebrow">Timeline</span>
        </div>
        {placed.map(({ t }) => (
          <div className="tl-name" key={t.id}>
            <span className={`kind-dot ${t.sourceKind === 'microphone' ? 'audio' : t.sourceKind === 'screen' ? 'screen' : ''}`} />
            <span className="ellipsis">
              <strong>{t.participantName}</strong> · {t.label}
            </span>
          </div>
        ))}
      </div>
      <div className="tl-tracks" style={{ position: 'relative' }}>
        <div className="tl-head">
          {ticks.map((ms) => (
            <span key={ms} className="tick" style={{ left: pct(ms) }}>
              {formatDuration(ms)}
            </span>
          ))}
        </div>
        {ticks.slice(1).map((ms) => (
          <span key={ms} className="grid-line" style={{ left: pct(ms) }} />
        ))}
        {placed.map(({ t, start, len }) => (
          <div className="tl-lane" key={t.id}>
            <i
              className={t.sourceKind === 'microphone' ? 'audio' : t.sourceKind === 'screen' ? 'screen' : ''}
              style={{ left: pct(start), width: pct(len) }}
              title={`${t.participantName} · ${t.label}: starts +${(start / 1000).toFixed(2)} s, ${formatDuration(len)} long`}
            />
            {t.syncMarks.map(([, server]) => (
              <span key={server} className="mark" style={{ left: pct(server - take.startedAt) }} title={`Sync mark at ${formatDuration(server - take.startedAt)}`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
